import { client } from '../../../../configs/apollo-client';
import {
  CheckUserEventOnDayPromptCompletedDocument,
  GetUserEventOnDayPromptDocument,
  type GetUserEventOnDayPromptQuery,
  type GetUserEventOnDayPromptQueryVariables,
  type CheckUserEventOnDayPromptCompletedQuery,
  type CheckUserEventOnDayPromptCompletedQueryVariables,
} from '@/entities/on-day-prompt';
import { type ApolloError } from '@apollo/client';
import { LoadingScreen, showError } from '@drisk-ai/ui';
import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  checkUserPrivileges,
  GROUPED_PRIVILEGES,
  SystemPrivilege,
} from '@/entities/authentication';
import { privilegeGuardRoute } from '@/features/authenticate';
import { ViewEventPage } from '@/pages/events';
import { EventTaskStoreProvider } from '@/entities/task';
import { accessEventGuardRoute } from '@/features/event/view-detail';

export const Route = createFileRoute('/_private/events/$eventUuid/')({
  component: () => (
    <EventTaskStoreProvider>
      <ViewEventPage />
    </EventTaskStoreProvider>
  ),

  async beforeLoad(ctx) {
    await accessEventGuardRoute({
      eventUuid: ctx.params.eventUuid,
      client: client,
    });
    // Skip check if only search params changed (tab navigation)
    // ctx.cause is 'enter' on initial load, 'stay' on search param changes
    if (ctx.cause === 'stay') {
      return;
    }

    privilegeGuardRoute({ privileges: GROUPED_PRIVILEGES.eventDetail });

    const eventUuid = ctx.params.eventUuid;

    const hasPrivilegeToUpsertOnDayPrompt = checkUserPrivileges([
      SystemPrivilege.ManageOnDayPrompt,
    ]);

    if (!hasPrivilegeToUpsertOnDayPrompt) {
      return;
    }

    try {
      const { data, errors } = await client.query<
        CheckUserEventOnDayPromptCompletedQuery,
        CheckUserEventOnDayPromptCompletedQueryVariables
      >({
        query: CheckUserEventOnDayPromptCompletedDocument,
        variables: {
          input: {
            eventUuid,
          },
        },
      });

      if (errors?.[0]) {
        // Allow user to view event normally without entering prompt when event not started or closed
        if (
          errors[0].message.includes('Event is not started') ||
          errors[0].message.includes('Event is ended')
        ) {
          return;
        } else {
          showError(errors[0].message);
          return redirect({ to: '/events', replace: true });
        }
      }

      if (data && !data.checkUserEventOnDayPromptCompleted.isCompleted) {
        const { data, errors } = await client.query<
          GetUserEventOnDayPromptQuery,
          GetUserEventOnDayPromptQueryVariables
        >({
          query: GetUserEventOnDayPromptDocument,
          variables: {
            input: {
              eventUuid,
            },
          },
        });

        if (errors?.[0]) {
          return redirect({ to: `/on-day-prompt/${eventUuid}` });
        }

        // Allow user to access event detail page when they have entered at least 1 prompt question
        if (data.getUserEventOnDayPrompt.uuid) {
          return;
        }

        return redirect({ to: `/on-day-prompt/${eventUuid}` });
      }
    } catch (error) {
      showError((error as ApolloError).message);
      redirect({ to: '/events', replace: true });
    }
  },
  pendingComponent: () => <LoadingScreen />,
});
